/**
 * "Open when the box starts" (AutostartPlugin.java). On the website, in the Play build and in an
 * APK older than the plugin it all answers null / does nothing, so Settings shows no row.
 */

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  PluginHeaders?: Array<{ name: string }>;
}

const PLUGIN = "Autostart";

export interface AutostartState {
  /** Only SliqueTV's direct-download build offers it. */
  supported: boolean;
  enabled: boolean;
  /** Android 10+ opens an app from the background only with "Display over other apps". */
  permitted: boolean;
}

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

function readState(answer: unknown): AutostartState | null {
  const value = answer as Partial<AutostartState> | null;
  if (!value || typeof value.supported !== "boolean") return null;
  return { supported: value.supported, enabled: value.enabled === true, permitted: value.permitted === true };
}

export async function autostartState(): Promise<AutostartState | null> {
  const cap = capacitor();
  if (!cap) return null;
  try {
    return readState(await cap.nativePromise!(PLUGIN, "get", {}));
  } catch {
    return null;
  }
}

export async function setAutostart(enabled: boolean): Promise<AutostartState | null> {
  const cap = capacitor();
  if (!cap) return null;
  try {
    return readState(await cap.nativePromise!(PLUGIN, "set", { enabled }));
  } catch {
    return null;
  }
}

/** Opens Android's "Display over other apps" screen for this app. */
export async function openAutostartPermission(): Promise<"opened" | "unavailable"> {
  const cap = capacitor();
  if (!cap) return "unavailable";
  try {
    await cap.nativePromise!(PLUGIN, "openPermission", {});
    return "opened";
  } catch {
    return "unavailable";
  }
}
